import { call } from '@rental/contracts/client'
import { RESERVATION_STATUSES } from '@rental/domain'
import { format, plural } from '@rental/i18n'
import { Field } from '@rental/ui'
import { keepPreviousData, useQuery } from '@tanstack/react-query'
import { getRouteApi } from '@tanstack/react-router'
import { CalendarDays, SearchX } from 'lucide-react'
import { useId } from 'react'
import { can } from '../access'
import { useAccess } from '../access-context'
import { api, load } from '../api'
import { BookingItem } from '../components/BookingItem'
import { EmptyState } from '../components/EmptyState'
import { PageHeader } from '../components/PageHeader'
import { ResultCount, ResultList } from '../components/Results'
import { SearchField, SearchForm, useSearchFields } from '../components/SearchForm'
import { Guard, LoadState } from '../components/states'
import { FieldPair, PAIRED_FIELD, StaffInput, StaffSelect } from '../components/ui'
import { useLocale } from '../i18n'
import { problemOf } from '../problems'
import type { BookingsSearch } from '../router'

const route = getRouteApi('/bookings')

/**
 * The bookings list, for whoever reads bookings (UI-2g): anyone else who types its address reads
 * the refusal under its title, and nothing is asked of the server for it.
 */
export function BookingsScreen() {
  const { messages: m } = useLocale()
  const { access } = useAccess()
  return (
    <Guard allowed={can(access, 'bookings.read')} title={m.bookings.title}>
      <BookingsList />
    </Guard>
  )
}

/** The bookings list: what is coming, or a search by code or customer. */
function BookingsList() {
  const { locale, messages: m } = useLocale()
  const search = route.useSearch()
  const navigate = route.useNavigate()
  const asked = {
    q: search.q ?? '',
    status: search.status ?? '',
    from: search.from ?? '',
    to: search.to ?? '',
  }
  // "Trova" in the menu can change the search from outside the form: the form follows.
  const [form, setForm] = useSearchFields(asked)
  const page = search.page ?? 1
  const list = useQuery({
    queryKey: ['bookings', search],
    queryFn: () =>
      load(
        call(
          api.GET('/v1/staff/bookings', {
            params: {
              query: {
                ...(search.q ? { q: search.q } : {}),
                ...(search.status ? { status: search.status } : {}),
                ...(search.from ? { from: search.from } : {}),
                ...(search.to ? { to: search.to } : {}),
                page,
              },
            },
          }),
        ),
      ),
    placeholderData: keepPreviousData,
  })
  const problem = problemOf(list.error)
  const data = list.data
  const fetching = list.isPlaceholderData
  const unfiltered = !search.q && !search.status && !search.from && !search.to
  const countId = useId()

  function submit() {
    const next: BookingsSearch = {
      ...(form.q.trim() ? { q: form.q.trim() } : {}),
      ...(form.status ? { status: form.status as BookingsSearch['status'] } : {}),
      ...(form.from ? { from: form.from } : {}),
      ...(form.to ? { to: form.to } : {}),
    }
    void navigate({ to: '/bookings', search: next })
  }

  // More than one page, or a page past the end (an old link): the pages show.
  const paged = data ? data.total > data.pageSize || page > 1 : false
  const first = data ? (data.page - 1) * data.pageSize + 1 : 0
  return (
    <>
      <PageHeader title={m.bookings.title}>
        <div className="flex flex-col gap-4 pt-2">
          <SearchForm
            label={m.bookings.title}
            filtered={!unfiltered}
            onSubmit={submit}
            onClear={() => void navigate({ to: '/bookings', search: {} })}
          >
            <SearchField
              label={m.bookings.search}
              hint={m.bookings.searchHint}
              value={form.q}
              onChange={(q) => setForm({ ...form, q })}
            />
            <Field label={m.bookings.status} className="grow sm:max-w-xs">
              {(control) => (
                <StaffSelect
                  {...control}
                  value={form.status}
                  onChange={(event) => setForm({ ...form, status: event.target.value })}
                >
                  <option value="">{m.bookings.anyStatus}</option>
                  {RESERVATION_STATUSES.map((status) => (
                    <option key={status} value={status}>
                      {m.status[status]}
                    </option>
                  ))}
                </StaffSelect>
              )}
            </Field>
            {/* The period's two days, one unit: never one at a row's end and one on the next (UI-2g). */}
            <FieldPair>
              <Field label={m.bookings.from} className={PAIRED_FIELD}>
                {(control) => (
                  <StaffInput
                    {...control}
                    type="date"
                    value={form.from}
                    onChange={(event) => setForm({ ...form, from: event.target.value })}
                  />
                )}
              </Field>
              <Field label={m.bookings.to} className={PAIRED_FIELD}>
                {(control) => (
                  <StaffInput
                    {...control}
                    type="date"
                    value={form.to}
                    onChange={(event) => setForm({ ...form, to: event.target.value })}
                  />
                )}
              </Field>
            </FieldPair>
          </SearchForm>
          <ResultCount
            id={countId}
            count={data ? plural(locale, m.bookings.found, data.total) : null}
            range={
              data && paged && data.items.length > 0
                ? format(m.lists.range, { first, last: first + data.items.length - 1 })
                : null
            }
            asked={JSON.stringify(asked)}
            fetching={fetching}
            note={unfiltered ? m.bookings.upcoming : null}
          />
        </div>
      </PageHeader>

      <LoadState data={data} problem={problem}>
        {(data) =>
          data.total === 0 ? (
            unfiltered ? (
              <EmptyState icon={CalendarDays} title={m.bookings.noneUpcoming}>
                {m.bookings.fills}
              </EmptyState>
            ) : (
              <EmptyState icon={SearchX} title={m.bookings.none}>
                {m.lists.change}
              </EmptyState>
            )
          ) : (
            <ResultList
              countId={countId}
              fetching={fetching}
              pages={
                paged
                  ? {
                      page,
                      shown: data.page,
                      rows: data.items.length,
                      pageSize: data.pageSize,
                      total: data.total,
                      // The first page is the list's own address, with no page in it.
                      onPage: (next) =>
                        void navigate({
                          to: '/bookings',
                          search: { ...search, page: next > 1 ? next : undefined },
                        }),
                    }
                  : undefined
              }
            >
              {data.items.length === 0 ? (
                <li className="py-3 text-text-muted">{m.lists.pastEnd}</li>
              ) : (
                data.items.map((row) => (
                  <BookingItem key={row.code} row={row} kind="pickup" when="date-time" />
                ))
              )}
            </ResultList>
          )
        }
      </LoadState>
    </>
  )
}
